import {
  type Analytics,
  initializeAnalytics,
  isSupported,
  logEvent,
  setDefaultEventParameters,
} from 'firebase/analytics'
import { type FirebaseApp } from 'firebase/app'

import { handleNotAwaitedPromise } from '@/sentry/handleNotAwaitedPromise.ts'

let analytics: Promise<Analytics | null> = Promise.resolve(null)

// GA4 only runs in a deployed build: `pnpm dev` and the emulator modes share the dev stream's
// measurement ID, and their traffic would pollute it.
const analyticsEnabled = () =>
  import.meta.env.PROD && !import.meta.env.VITE_EMULATOR_HOST && !!import.meta.env.VITE_FIREBASE_MEASUREMENT_ID

// The dashboard's URLs carry identifiers (judicial records, teams, transport requests) and its
// search params free text: GA4 only ever sees the route template, `/judicial/$recordId`.
const pageLocation = (routePath: string) => `${globalThis.location.origin}${routePath}`

const pageReferrer = () => {
  try {
    return document.referrer ? new URL(document.referrer).origin : null
  } catch {
    return null
  }
}

const loadAnalytics = async (app: FirebaseApp) => {
  try {
    if (!(await isSupported())) return null
    return initializeAnalytics(app, {
      config: {
        send_page_view: false,
        page_location: pageLocation('/'),
        page_referrer: pageReferrer(),
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      },
    })
  } catch {
    // A browser without IndexedDB or cookies leaves the dashboard without analytics.
    return null
  }
}

export const initAnalytics = (app: FirebaseApp) => {
  if (analyticsEnabled()) {
    analytics = loadAnalytics(app)
  }
}

const sendPageView = async (routePath: string) => {
  const instance = await analytics
  if (!instance) return

  const location = pageLocation(routePath)
  // Every later event (user_engagement, scroll…) reports this location instead of the URL.
  setDefaultEventParameters({ page_location: location })
  logEvent(instance, 'page_view', { page_location: location, page_title: document.title })
}

export const logPageView = (routePath: string) => {
  handleNotAwaitedPromise(sendPageView(routePath))
}
